import type { CustomerRestrictionTypes } from 'stores/customer/interfaces';
import type { UserMenuItem } from '../../interfaces';
import { openForbiddenModal } from 'shared/ForbiddenActionModal';
import { GetIncentiveCampaignsFlagsResponse } from 'services/campaigns/interfaces';
import { HeaderProps } from 'components/Header/interfaces';

const userMenuItems = {
	posList: {
		icon: 'graph_5',
		text: 'Lojas vinculadas',
		linkTo: '/minhas-farmacias',
	} as UserMenuItem,
	posInformation: {
		icon: 'storefront',
		text: 'Dados das lojas',
		linkTo: '/minhas-farmacias/dados',
	} as UserMenuItem,
	manageAccess: {
		icon: 'key_vertical',
		text: 'Permissões de acesso',
		linkTo: '/minhas-farmacias/acessos',
	} as UserMenuItem,
	distributors: {
		icon: 'store',
		text: 'Distribuidores',
		linkTo: '/minhas-farmacias/distribuidores',
	} as UserMenuItem,
	orderImport: {
		icon: 'upload_file',
		text: 'Importação de pedido',
		linkTo: '/importacao-pedido',
	} as UserMenuItem,
	orders: {
		icon: 'list_alt',
		text: 'Meus pedidos',
		linkTo: '/pedidos',
	} as UserMenuItem,
	incentiveCampaign: {
		icon: 'rocket_launch',
		text: 'Extrato Bateu, Ganhou',
		linkTo: '/bateu-ganhou/extrato',
	} as UserMenuItem,
};

const allAllowed = {
	orders: true,
	posList: true,
	orderImport: true,
	manageAccess: true,
	distributors: true,
	posInformation: true,
	incentiveCampaign: true,
};

const restrictionAllowedItems: Record<
	CustomerRestrictionTypes,
	Partial<Record<keyof typeof userMenuItems, boolean>>
> = {
	PARTNER: allAllowed,
	NO_LINKS: { posList: true },
	SIMULATED: {
		orders: true,
		orderImport: true,
		distributors: true,
		manageAccess: true,
		posInformation: true,
		incentiveCampaign: true,
	},

	QUOTATION: {},
	'2FA_EXPIRED': {},
};

export const getMenuItems = ({
	selectedPosId,
	customerRestriction,
	incentiveCampaignFlags,
	orderImportControls,
}: {
	selectedPosId?: string;
	customerRestriction?: CustomerRestrictionTypes;
	incentiveCampaignFlags?: GetIncentiveCampaignsFlagsResponse;
} & Pick<HeaderProps, 'orderImportControls'>): UserMenuItem[] => {
	const allowedItems = customerRestriction ? restrictionAllowedItems[customerRestriction] : allAllowed;

	const menuItems = Object.entries(userMenuItems).reduce((acc, [key, data]) => {
		const newData = { ...data };
		const itemKey = key as keyof typeof userMenuItems;

		//! CONDITIONS TO HIDE ITEMS

		if (
			!incentiveCampaignFlags?.hasCurrentCampaign &&
			!incentiveCampaignFlags?.hasCampaignHistory &&
			itemKey === 'incentiveCampaign'
		) {
			// Show `Bateu, Ganhou` only for PDVs with campaign history
			return acc;
		}

		// Always hide `Orders Page` when no POS is selected
		if (!selectedPosId && itemKey === 'orders') return acc;
		if (!orderImportControls.visible && itemKey === 'orderImport') return acc;

		if (customerRestriction === 'SIMULATED') {
			// Hide some menu items when no POS is selected
			if (
				!selectedPosId &&
				(['distributors', 'manageAccess', 'posInformation'] as Array<typeof itemKey>).includes(itemKey)
			) {
				return acc;
			}
		}

		//* TREATMENTS IN ITEMS THAT WILL BE SHOWN

		// Append selected POS ID to distributors link if POS is selected
		if (selectedPosId && itemKey === 'distributors') {
			newData.linkTo = `${userMenuItems.distributors.linkTo}?f=${selectedPosId}`;
		}

		if (customerRestriction === 'PARTNER') {
			if ((['posList'] as Array<typeof itemKey>).includes(itemKey)) {
				newData.onClick = openForbiddenModal;
			}
		}

		if (itemKey === 'orderImport') newData.onClick = orderImportControls.onClick;

		// Push item in menu list
		if (allowedItems[itemKey]) acc.push(newData);

		return acc;
	}, [] as UserMenuItem[]);

	return menuItems;
};
